import { useState } from "react";
import { useImageDropZone } from "../../../hooks/useImageDropZone.ts";
import { useAppMutation } from "../../../http/useAppMutation.ts";
import { useWorkspaceUuid } from "../../hooks/useWorkspaceUuid.ts";
import type {
  BaseModel,
  ModelType,
  StyleModelImagesKind,
} from "../../types.ts";
import { useUploadMultipleImages } from "./useUploadMultipleImages.ts";

export const useModelUpload = (
  modelType: ModelType,
  maxFiles: number,
  onModelAdd: (modelUuid: string, isModelTrained: boolean) => void,
  getImagesKind?: () => StyleModelImagesKind | undefined,
) => {
  const workspaceUuid = useWorkspaceUuid();
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const {
    mutation: {
      mutate: createReferenceFromContents,
      isLoading: isCreateReferenceFromContentsLoading,
    },
  } = useAppMutation({
    path: `workspaces/${workspaceUuid}/references/upload`,
    invalidate: [`workspaces/${workspaceUuid}/references`],
    waitForInvalidation: true,
    onCacheUpdated: (response: { data: BaseModel }) => {
      onModelAdd(response.data.uuid, response.data.is_trained);
    },
  });

  const { mutate: uploadMultiple, isLoading: isUploadLoading } =
    useUploadMultipleImages({
      onAllImagesUploaded: (contentUuids) => {
        const currentImagesKind = getImagesKind?.();
        createReferenceFromContents({
          image_uuids: contentUuids,
          model_type: modelType,
          ...(currentImagesKind && { images_kind: currentImagesKind }),
        });
        setPendingFiles([]);
      },
      describeImage: true,
    });

  const { open: openFileBrowser, getInputProps } = useImageDropZone({
    multiple: true,
    minImageSize: 256,
    maxFiles,
    onImagesValidated: (files) => {
      const fileBlobs = files.map((file) => file.fileToUpload);
      setPendingFiles(fileBlobs);
      uploadMultiple({
        images: fileBlobs,
      }).catch((error) => {
        console.error("Failed to upload images:", error);
        setPendingFiles([]);
      });
    },
  });

  const isLoading =
    isCreateReferenceFromContentsLoading ||
    isUploadLoading ||
    pendingFiles.length > 0;

  return {
    openFileBrowser,
    getInputProps,
    isLoading,
  };
};
